import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, TextField, Button, Stack, Autocomplete,
  Checkbox, FormControlLabel, IconButton, Alert, CircularProgress, Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { COUNTRIES } from '../../../constants/countries';
import CaptchaWidget from '../../../components/CaptchaWidget';

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    color: '#eef1ff', background: 'rgba(255,255,255,0.04)', borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.14)' },
    '&:hover fieldset': { borderColor: 'rgba(59,130,246,0.6)' },
    '&.Mui-focused fieldset': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59,130,246,0.18)' },
  },
  '& .MuiInputLabel-root': { color: '#9aa6b8' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
  '& .MuiFormHelperText-root': { color: '#ff8a9b' },
};

/**
 * "Join the waitlist" — a professional glassmorphism modal. The captcha is the
 * Cloudflare Turnstile widget rendered from a build-time site key (no backend
 * config call); the form submits to the waitlist API which verifies the token.
 */
const WaitlistModal = ({ open, onClose }) => {
  // Captcha config comes from the backend (provider + site key + enabled).
  const [captcha, setCaptcha] = useState({ enabled: false });
  useEffect(() => {
    if (!open) return;
    fetch('/api/auth/captcha-config')
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then(setCaptcha)
      .catch(() => setCaptcha({ enabled: false }));
  }, [open]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState(null);
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [updates, setUpdates] = useState(true);
  const [token, setToken] = useState(null);
  const [errs, setErrs] = useState({});
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const reset = () => {
    setName(''); setEmail(''); setCountry(null); setPhone(''); setReason('');
    setUpdates(true); setToken(null); setErrs({}); setError(null); setDone(null);
  };
  const close = () => { onClose(); setTimeout(reset, 250); };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const fe = {};
    if (!name.trim()) fe.name = 'Name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) fe.email = 'Enter a valid email';
    if (!country) fe.country = 'Country is required';
    setErrs(fe);
    if (Object.keys(fe).length) return;
    const isV3 = captcha.provider === 'RECAPTCHA_V3';
    if (captcha.enabled && !isV3 && !token) { setError('Please complete the verification'); return; }

    setSubmitting(true);
    try {
      let captchaToken = token;
      if (captcha.enabled && isV3 && window.grecaptcha) {
        captchaToken = await new Promise((resolve) => {
          window.grecaptcha.ready(() =>
            window.grecaptcha.execute(captcha.siteKey, { action: 'waitlist' }).then(resolve));
        });
      }
      const res = await fetch('/api/v1.0/waitlist/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(), name: name.trim(), country: country.code,
          phone: phone.trim() || null, productUpdatesOptIn: updates,
          interestReason: reason.trim() || null, captchaToken: captchaToken || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!body.success) setError(body.message || 'Something went wrong, please try again.');
      else setDone(body);
    } catch {
      setError('Could not reach the server. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={close} maxWidth="sm" fullWidth keepMounted
      PaperProps={{ sx: {
        background: 'rgba(16,19,40,0.85)', backdropFilter: 'blur(22px) saturate(140%)',
        border: '1px solid rgba(255,255,255,0.10)', borderRadius: 4,
        boxShadow: '0 40px 120px rgba(0,0,0,0.6)', overflow: 'hidden', color: '#eef1ff',
      } }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(5,6,15,0.6)', backdropFilter: 'blur(4px)' } }}
    >
      {/* gradient top accent */}
      <Box sx={{ height: 4, background: 'linear-gradient(90deg,#2563eb,#3b82f6,#10b981)' }} />
      <IconButton onClick={close} aria-label="Close"
        sx={{ position: 'absolute', top: 10, right: 10, color: '#9aa3c7', '&:hover': { color: '#fff' } }}>
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        {done ? (
          <Fade in>
            <Stack alignItems="center" textAlign="center" spacing={1.5} sx={{ py: 3 }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 56, color: done.created ? '#10b981' : '#e6c200' }} />
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {done.created ? "You're on the list" : "Already on the list"}
              </Typography>
              <Typography sx={{ color: '#9aa3c7', maxWidth: 380 }}>
                {done.created
                  ? "Thanks for your interest in VerOps. We'll email you the moment a slot opens up."
                  : "We've kept your existing entry — we'll reach out to the same address when slots free up."}
              </Typography>
              <Button onClick={close} variant="outlined" sx={{ mt: 1, color: '#eef1ff',
                borderColor: 'rgba(255,255,255,0.2)', textTransform: 'none', borderRadius: 2 }}>
                Done
              </Button>
            </Stack>
          </Fade>
        ) : (
          <Box component="form" onSubmit={submit} noValidate>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 0.5 }}>
              <AutoAwesomeRoundedIcon sx={{ color: '#60a5fa' }} />
              <Typography variant="h5" sx={{ fontWeight: 800,
                background: 'linear-gradient(90deg,#fff,#bcd2ff 50%,#86efac)',
                WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
                Join the waitlist
              </Typography>
            </Stack>
            <Typography sx={{ color: '#9aa3c7', mb: 2.5, fontSize: 14.5 }}>
              Be first to get access to VerOps — one platform for synthetic, APM, RUM,
              Workbooks and AI‑quality monitoring.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(239,68,68,0.12)',
              color: '#ffd7dd', border: '1px solid rgba(239,68,68,0.3)' }}>{error}</Alert>}

            <Stack spacing={2}>
              <TextField label="Full name" value={name} onChange={(e) => setName(e.target.value)}
                error={!!errs.name} helperText={errs.name} fullWidth size="small" sx={FIELD_SX} required />
              <TextField label="Work email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                error={!!errs.email} helperText={errs.email} fullWidth size="small" sx={FIELD_SX} required />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Autocomplete
                  options={COUNTRIES} value={country} onChange={(_, v) => setCountry(v)}
                  getOptionLabel={(o) => o?.name || ''} isOptionEqualToValue={(a, b) => a.code === b.code}
                  fullWidth size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="Country" error={!!errs.country} helperText={errs.country}
                      sx={FIELD_SX} required />
                  )}
                  componentsProps={{ paper: { sx: { bgcolor: '#12162e', color: '#eef1ff' } } }} />
                <TextField label="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)}
                  fullWidth size="small" sx={FIELD_SX} />
              </Stack>
              <TextField label="What are you hoping to monitor? (optional)" value={reason}
                onChange={(e) => setReason(e.target.value)} fullWidth size="small" multiline minRows={2} sx={FIELD_SX} />

              <FormControlLabel
                control={<Checkbox checked={updates} onChange={(e) => setUpdates(e.target.checked)}
                  sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#3b82f6' } }} />}
                label={<Typography sx={{ color: '#9aa3c7', fontSize: 13.5 }}>Send me occasional product updates</Typography>} />

              {captcha.enabled && (
                <CaptchaWidget provider={captcha.provider} siteKey={captcha.siteKey}
                  onVerify={setToken} onExpire={() => setToken(null)} />
              )}

              <Button type="submit" disabled={submitting} fullWidth size="large"
                sx={{ mt: 0.5, py: 1.3, borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontSize: 16,
                  color: '#fff', background: 'linear-gradient(135deg,#2563eb,#10b981)',
                  boxShadow: '0 10px 30px rgba(37,99,235,0.35)',
                  '&:hover': { background: 'linear-gradient(135deg,#1d4ed8,#059669)' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.6)', background: 'rgba(37,99,235,0.5)' } }}>
                {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Request access'}
              </Button>
              <Typography sx={{ color: '#6b7299', fontSize: 12, textAlign: 'center' }}>
                No spam. We'll only email you about your waitlist spot.
              </Typography>
            </Stack>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistModal;
